'use client';
import React from 'react';
import { motion } from 'framer-motion';

const capabilities = [
    {
        title: "Multi-color Printing",
        description: "Achieve deep, vibrant brand colors with our high-channel printing process including solid white, orange, violet, and spot color matching.",
        icon: (
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.172-1.172a4 4 0 115.656 5.656l-1.172 1.172" />
            </svg>
        )
    },
    {
        title: "High-Speed Production",
        description: "Scale from small boutique runs to millions of units seamlessly with machines optimized for maximum throughput without quality loss.",
        icon: (
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
        )
    },
    {
        title: "Precision Registration",
        description: "Our AI-assisted registration ensures layers, foils, and die-cuts align within 0.1mm tolerance for a sharp, professional finish.",
        icon: (
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
        )
    },
    {
        title: "Specialty Finishes",
        description: "From tactile UV textures to high-build embossing, our finishing units create sensory experiences that set your brand apart on the shelf.",
        icon: (
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-7.714 2.143L11 21l-2.286-6.857L1 12l7.714-2.143L11 3z" />
            </svg>
        )
    },
    {
        title: "Substrate Compatibility",
        description: "Print on paper, PE, PP, PET, metallics, and biodegradable films with specialized surface treatments for maximum ink adhesion.",
        icon: (
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
        )
    },
    {
        title: "In-line Inspection",
        description: "100% automated inspection cameras scan every single label on the fly, flagging even the smallest imperfections for removal.",
        icon: (
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
        )
    }
];

const Capabilities = () => {
    return (
        <section className="bg-white py-24">
            <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-3xl mx-auto mb-16 text-center">
                    <h2 className="text-4xl md:text-5xl font-display font-bold text-[#09172e] mb-6">
                        Engineered Excellence in Every Print.
                    </h2>
                    <p className="text-gray-600 text-lg">
                        Our infrastructure is more than just machines. It's an integrated ecosystem of hardware and software designed to provide the highest standards of reliability and creativity.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {capabilities.map((cap, index) => (
                        <motion.div
                            key={index}
                            className="p-8 border border-gray-100 hover:border-[#2e8b8d]/30 hover:shadow-2xl hover:shadow-[#2e8b8d]/5 transition-all duration-500 bg-gray-50/50 group"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.1 }}
                        >
                            <div className="text-[#2e8b8d] mb-6 group-hover:scale-110 transition-transform duration-300">
                                {cap.icon}
                            </div>
                            <h3 className="text-xl font-display font-bold text-[#09172e] mb-4">
                                {cap.title}
                            </h3>
                            <p className="text-gray-600 text-sm leading-relaxed">
                                {cap.description}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Capabilities;
